import { useState } from "react";

import { format } from "date-fns";

import { parseISO, formatDistanceToNow } from "date-fns";
import { toZonedTime } from "date-fns-tz";

import {
    Sheet,
    SheetContent,
    SheetFooter,
    SheetHeader,
} from "../Components/ui/sheet.tsx";
import { Button } from "./ui/button.tsx";
import {
    ChevronLeftIcon,
    ChevronRightIcon,
    DownloadIcon,
    TrashIcon,
    XIcon,
    LoaderCircle,
} from "lucide-react";
import { useFormInsightsContext } from "../Contexts/FormInsightsContext.tsx";
import {
    QUESTION_OBJECTS,
    QUESTION_TYPES,
} from "../Constants/questionTypes.js";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApi } from "../Api/useApi.ts";
import DeleteResponseConfirmationModal from "./DeleteResponseConfimationModal.tsx";
import Markdown from "react-markdown";
import { useParams } from "react-router-dom";

type ResponseModalProps = {
    open: boolean;
    onClose: () => void;
};

function ResponseModal({ open, onClose }: ResponseModalProps) {
    const {
        form,
        fields,
        totalResponses,
        responses,
        activeRow,
        setActiveRow,
        openDeleteConfirmationModal,
        setOpenDeleteConfirmationModal,
    } = useFormInsightsContext();

    const { workspaceId } = useParams();

    const getResponse = (responseId: string) => {
        return responses?.find(
            (response) => response?.response_id === responseId,
        );
    };

    function getRelativeTimeWithFormattedDate(serverTime: string): string {
        if (!serverTime) {
            console.error("Invalid timestamp:", serverTime);
            return "Invalid timestamp";
        }

        const correctedTimestamp = serverTime.endsWith("Z")
            ? serverTime
            : `${serverTime}Z`;

        const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        try {
            const parsedServerTime = parseISO(correctedTimestamp);

            const zonedTime = toZonedTime(parsedServerTime, userTimeZone);

            // Format like: 17 Oct 2024 - 13:02
            const formattedDate = format(zonedTime, "dd MMM yyyy - HH:mm");

            // Relative like: 15 minutes ago
            const relativeTime = formatDistanceToNow(zonedTime, {
                addSuffix: true,
            });

            return `${formattedDate}`;
        } catch (error) {
            console.error("Error parsing timestamp:", error);
            return "Invalid timestamp format";
        }
    }
    const getQuestionFields = (response: any) => {
        const out: any[] = [];
        for (const field of fields) {
            const question = response?.data?.find(
                (qa) => qa.question.question_id === field.id,
            );
            const answers = question?.answer ?? [];

            const questionTitle = field?.title;
            const questionType = field?.type;
            if (questionType === "date")
                out.push({
                    id: field?.id,
                    title: "Date",
                    type: "date",
                    answer: response?.updated_at
                        ? getRelativeTimeWithFormattedDate(response?.updated_at)
                        : "",
                });
            else if (questionType === "picture_choice")
                out.push({
                    id: field?.id,
                    title: questionTitle,
                    type: questionType,
                    answer: answers,
                });
            else if (questionType === "status")
                out.push({
                    id: field?.id,
                    title: "Response State",
                    type: "status",
                    answer: response?.type,
                });
            else if (questionType === "file_upload")
                out.push({
                    id: field?.id,
                    title: questionTitle,
                    type: questionType,
                    answer: answers,
                });
            else if (questionType === "ai_statement")
                out.push({
                    id: field?.id,
                    title: "AI Statement",
                    type: questionType,
                    answer: answers?.map((answer) => answer?.answer_text),
                });
            else
                out.push({
                    id: field?.id,
                    title: questionTitle,
                    type: questionType,
                    answer: answers?.map((answer) => answer?.answer_text),
                });
        }
        return out;
    };

    const api = useApi();
    const queryClient = useQueryClient();
    const [response, setResponse] = useState<any>(getResponse(activeRow));
    const [questionFields, setQuestionFields] = useState<any[]>(
        getQuestionFields(response),
    );

    const { mutate: deleteResponsesMutation, isPending } = useMutation({
        mutationFn: ({
            formId,
            responseIds,
        }: {
            formId: string;
            responseIds: string[];
        }) => api.responses.deleteResponses(formId, responseIds),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["formResponses"] });
            setOpenDeleteConfirmationModal(false);
            handleOnNext();
        },
        onError: (error) => {
            //messageApi.error("Could not delete form");
            console.error("Could not delete form", error);
        },
    });

    const { mutate: exportResponsesMutation, isPending: isExporting } =
        useMutation({
            mutationFn: ({
                workspaceId,
                formId,
                responseIds,
            }: {
                formId?: string;
                workspaceId: string;
                responseIds?: string[];
            }) => api.forms.exportResponses(workspaceId, formId, responseIds),
            onSuccess: (data) => {
                const exportFile = data?.data?.file_url;
                if (exportFile) {
                    const anchor = document.createElement("a");
                    anchor.href = exportFile;
                    anchor.setAttribute("download", "");
                    document.body.appendChild(anchor);
                    anchor.click();
                    document.body.removeChild(anchor);
                }
                queryClient.invalidateQueries({ queryKey: ["responses"] });
            },
        });

    const currentIndex = responses?.findIndex(
        (res) => res?.response_id === response?.response_id,
    );

    const handleOnNext = () => {
        const currentIndex = responses?.findIndex(
            (res) => res?.response_id === response?.response_id,
        );
        const nextResponseIndex = currentIndex + 1;
        if (nextResponseIndex > responses?.length) return;

        const nextResponse = responses[nextResponseIndex];
        if (!nextResponse) return;

        setResponse(nextResponse);
        setQuestionFields(getQuestionFields(nextResponse));
        setActiveRow(nextResponse?.response_id);
    };

    const handleOnPrev = () => {
        const currentIndex = responses?.findIndex(
            (res) => res?.response_id === response?.response_id,
        );
        const prevResponseIndex = currentIndex - 1;
        if (prevResponseIndex < 0) return;

        const prevResponse = responses[prevResponseIndex];
        if (!prevResponse) return;

        setResponse(prevResponse);
        setQuestionFields(getQuestionFields(prevResponse));
        setActiveRow(prevResponse?.response_id);
    };

    const handleOnExport = () => {
        exportResponsesMutation({
            workspaceId: workspaceId as string,
            formId: undefined,
            responseIds: [response?.response_id],
        });
    };

    const handleOnDelete = () => {
        deleteResponsesMutation({
            formId: form?._id,
            responseIds: [response?.response_id],
        });
    };

    //sheet side if mobile bottom else right
    const sheetSide = window.innerWidth < 1024 ? "bottom" : "right";

    return (
        <Sheet modal={false} open={open} onOpenChange={onClose}>
            <SheetContent
                side={sheetSide}
                className="absolute lg:top-[8.3rem] w-full bottom-0 h-[430px] lg:h-[calc(100vh-15.5%)] lg:rounded-l-[12px] rounded-t-[12px] shadow-md lg:w-[400px] [&>button]:hidden p-2 lg:p-4"
            >
                <SheetHeader className="flex flex-row items-center justify-between ">
                    <div className="flex flex-row items-center gap-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            className="p-0 w-fit size-9 focus-visible:ring-0"
                            disabled={currentIndex === 0}
                            onClick={handleOnPrev}
                        >
                            <ChevronLeftIcon className="size-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="p-0 w-fit size-9 focus-visible:ring-0"
                            disabled={currentIndex === totalResponses - 1}
                            onClick={handleOnNext}
                        >
                            <ChevronRightIcon className="size-4" />
                        </Button>
                        <div className="px-2">
                            <p className="text-xs">Response</p>
                            <p className="text-xs text-gray-600">
                                {currentIndex + 1} of {totalResponses}
                            </p>
                        </div>
                    </div>
                    <div className="flex flex-row items-center -mt-2 space-x-2">
                        <Button
                            variant="outline"
                            size="sm"
                            disabled={isPending}
                            className="p-0 w-fit size-9 lg:hidden"
                            onClick={() => setOpenDeleteConfirmationModal(true)}
                        >
                            <TrashIcon className="size-4" />
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="p-0 w-fit size-9"
                            onClick={handleOnExport}
                            disabled={isExporting}
                        >
                            {isExporting ? (
                                <LoaderCircle className="animate-spin size-4" />
                            ) : (
                                <DownloadIcon className="size-4" />
                            )}
                        </Button>
                        <Button
                            variant="outline"
                            size="sm"
                            className="p-0 w-fit size-9"
                            onClick={onClose}
                        >
                            <XIcon className="size-4" />
                        </Button>
                    </div>
                </SheetHeader>
                <div className="h-[calc(100%-100px)] w-full overflow-y-auto overflow-x-hidden no-scrollbar flex flex-col gap-4 mt-6">
                    {questionFields?.map((field) => (
                        <Field
                            key={field?.id}
                            question={field?.title}
                            questionType={field?.type}
                            answers={field?.answer}
                        />
                    ))}
                </div>

                <SheetFooter>
                    <Button
                        variant="outline"
                        size="sm"
                        disabled={isPending}
                        className="hidden p-0 w-fit size-9 lg:block"
                        onClick={() => setOpenDeleteConfirmationModal(true)}
                    >
                        <TrashIcon className="size-4" />
                    </Button>
                </SheetFooter>
                <DeleteResponseConfirmationModal
                    open={openDeleteConfirmationModal}
                    loading={isPending}
                    onCancel={() => setOpenDeleteConfirmationModal(false)}
                    onDelete={handleOnDelete}
                />
            </SheetContent>
        </Sheet>
    );
}

export default ResponseModal;

type FieldProps = {
    question: string;
    questionType: string;
    answers: any;
};

function Field({ question, questionType, answers }: FieldProps) {
    const q = QUESTION_OBJECTS[questionType];
    const Icon = q?.icon;
    const color = "#F1F2F4";

    if (questionType === QUESTION_TYPES.FILE_UPLOAD)
        return <FileUploadField question={question} files={answers} />;

    if (questionType === QUESTION_TYPES.PICTURECHOICE)
        return <PictureChoiceField question={question} answers={answers} />;

    const answersStr = Array.isArray(answers) ? answers?.join(", ") : answers;

    if (questionType === QUESTION_TYPES.AI_STATEMENT)
        return <AIStatementField text={answersStr} />;

    return (
        <div className="flex flex-row items-start gap-2">
            <div
                className={`flex justify-center items-center w-6 h-6 rounded-md bg-[${color}]`}
            >
                {Icon}
            </div>
            <div className="max-w-sm">
                <p className="text-sm text-muted-foreground max-w-[320px]">
                    {question}
                </p>
                <p className="mt-1 text-sm max-w-[320px] text-primary">
                    {answersStr || "-"}
                </p>
            </div>
        </div>
    );
}

type FileUploadFieldProps = {
    question: string;
    files: any[];
};

function FileUploadField({ question, files }: FileUploadFieldProps) {
    const color = "#F1F2F4";
    const q = QUESTION_OBJECTS["file_upload"];
    const Icon = q.icon;

    const { activeRow } = useFormInsightsContext();

    const api = useApi();
    const { urlId } = useParams();

    const getViewLinkMutation = useMutation({
        mutationFn: ({
            fileId,
            formId,
            responseId,
            fieldId,
            contentViewMethod,
        }: {
            fileId: string;
            formId: string;
            responseId: string;
            fieldId: string;
            contentViewMethod: string;
        }) =>
            api.files.viewFile(
                fileId,
                formId,
                responseId,
                fieldId,
                contentViewMethod,
            ),
        onSuccess: (data: any) => {
            const fileUrl = data?.data?.url;
            if (fileUrl) {
                const newTab = window.open(fileUrl, "_blank");
                if (newTab) {
                    newTab.focus();
                } else {
                    console.error("");
                }
            } else {
                console.error("");
            }
        },
        onError: (error: any) => {
            console.error("Could not get file", error);
        },
    });

    const handleOnView = (file: any) => {
        const fileId = file?.id;
        const formId = urlId as string;
        const responseId = activeRow as string;
        const fieldId = file?.field_id;
        const contentViewMethod = "inline";

        getViewLinkMutation.mutate({
            fileId,
            formId,
            responseId,
            fieldId,
            contentViewMethod,
        });
    };

    const getDownloadLinkMutation = useMutation({
        mutationFn: ({
            fileId,
            formId,
            responseId,
            fieldId,
            contentViewMethod,
        }: {
            fileId: string;
            formId: string;
            responseId: string;
            fieldId: string;
            contentViewMethod: string;
        }) =>
            api.files.viewFile(
                fileId,
                formId,
                responseId,
                fieldId,
                contentViewMethod,
            ),
        onSuccess: (data: any) => {
            const fileUrl = data?.data?.url;
            if (fileUrl) {
                const link = document.createElement("a");
                link.href = fileUrl;
                link.setAttribute("download", data?.data?.file_name);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            } else {
                console.error("");
            }
        },
        onError: (error: any) => {
            console.error("Could not get file", error);
        },
    });
    const handleOnDownload = (file: any) => {
        const fileId = file?.id;
        const formId = urlId as string;
        const responseId = activeRow as string;
        const fieldId = file?.field_id;
        const contentViewMethod = "attachment";

        getDownloadLinkMutation.mutate({
            fileId,
            formId,
            responseId,
            fieldId,
            contentViewMethod,
        });
    };

    return (
        <div className="flex flex-row items-start gap-2">
            <span
                className={`flex justify-center items-center w-6 h-6 rounded-md bg-[${color}]`}
            >
                {Icon}
            </span>
            <div>
                <p className="text-sm text-muted-foreground">{question}</p>
                {files.length === 0 && (
                    <p className="mt-1 text-sm text-primary">No documents</p>
                )}
                <div className="flex flex-col">
                    {files.map((file) => (
                        <div className="flex flex-row items-center gap-2">
                            <button onClick={() => handleOnView(file)}>
                                <p className="text-sm">{file?.file_name}</p>
                            </button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="p-1 w-7 h-7"
                                onClick={() => handleOnDownload(file)}
                            >
                                <DownloadIcon className="w-3 h-3" />
                            </Button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

type AIStatementFieldProps = {
    text: string; // Markdown text
};

function AIStatementField({ text }: AIStatementFieldProps) {
    const q = QUESTION_OBJECTS["file_upload"];
    const Icon = q.icon;
    const color = "#F1F2F4";

    return (
        <div className="flex flex-row items-start gap-2">
            <span
                className={`flex justify-center items-center w-6 h-6 rounded-md bg-[${color}]`}
            >
                {Icon}
            </span>
            <div className="max-w-sm">
                <p className="text-sm text-muted-foreground max-w-[320px]">
                    AI Statement
                </p>
                <Markdown className="text-sm break-words max-w-[320px]">
                    {text}
                </Markdown>
            </div>
        </div>
    );
}

function PictureChoiceField({
    question,
    answers,
}: {
    question: string;
    answers: any[];
}) {
    const color = "#F1F2F4";
    const q = QUESTION_OBJECTS["picture_choice"];
    const Icon = q.icon;

    return (
        <div className="flex flex-row items-start gap-2">
            <span
                className={`flex justify-center items-center w-6 h-6 rounded-md bg-[${color}]`}
            >
                {Icon}
            </span>
            <div>
                <p className="text-sm text-muted-foreground">{question}</p>

                <div className="flex flex-wrap gap-1.5 items-center mt-1">
                    {answers?.map((answer, index) => (
                        <div
                            key={index}
                            className="w-[89px] h-6 gap-1 flex items-center border border-[#DCDFE4] rounded-md"
                        >
                            <img
                                src={answer.url}
                                alt={answer.answer_text}
                                className="object-contain size-6 rounded-tl-md rounded-bl-md"
                            />
                            <span className="text-xs truncate">
                                {answer.answer_text}
                            </span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
